/**
 * Tab scoreboard.
 *
 * The table is built once: one row node per possible player, hidden by default
 * and filled in place. `update()` only writes text, display and classes.
 */
import { TEAM, TEAM_NAME } from '../../net/protocol.js';
import { el, setText, setNum, setClass, setStyle, setHidden } from './dom.js';

const PER_TEAM = 12;

function makeRow(parent) {
  const node = el('div', 'cs-sb-row', parent);
  const alive = el('i', 'cs-sb-alive', node);
  const name = el('span', 'cs-sb-name', node, '');
  const bomb = el('i', 'cs-sb-bomb', node, 'C4');
  const k = el('span', 'cs-sb-k', node, '0');
  const d = el('span', 'cs-sb-d', node, '0');
  const money = el('span', 'cs-sb-money', node);
  el('span', 'cs-sb-cur', money, '$');
  const moneyVal = el('span', 'cs-sb-mv', money, '0');
  const ping = el('span', 'cs-sb-p', node, '0');
  const row = { node, alive, name, bomb, k, d, money, moneyVal, ping };
  setHidden(node, true);
  return row;
}

function makeBlock(parent, teamCls, label) {
  const head = el('div', 'cs-sb-team ' + teamCls, parent);
  const name = el('span', 'cs-sb-tname', head, label);
  const count = el('span', 'cs-sb-tcount', head, '0');
  const cols = el('div', 'cs-sb-cols', parent);
  el('i', 'cs-sb-alive', cols);
  el('span', 'cs-sb-name', cols, 'PLAYER');
  el('i', 'cs-sb-bomb', cols);
  el('span', 'cs-sb-k', cols, 'K');
  el('span', 'cs-sb-d', cols, 'D');
  el('span', 'cs-sb-money', cols, '$');
  el('span', 'cs-sb-p', cols, 'PING');
  const body = el('div', 'cs-sb-body', parent);
  const rows = new Array(PER_TEAM);
  for (let i = 0; i < PER_TEAM; i++) rows[i] = makeRow(body);
  return { head, name, count, cols, body, rows };
}

export class Scoreboard {
  constructor(parent) {
    const root = el('div', 'cs-sb', parent);
    const box = el('div', 'cs-sb-box', root);

    const head = el('div', 'cs-sb-head', box);
    this.map = el('span', 'cs-sb-map', head, '');
    const score = el('div', 'cs-sb-score', head);
    this.tScore = el('span', 't', score, '0');
    this.ctScore = el('span', 'ct', score, '0');

    this.t = makeBlock(box, 't', TEAM_NAME[TEAM.T]);
    this.ct = makeBlock(box, 'ct', TEAM_NAME[TEAM.CT]);
    this.spec = makeBlock(box, 'spec', 'SPECTATORS');

    this.root = root;
  }

  update(s) {
    setText(this.map, s?.map ?? '');
    const sc = s?.score ?? null;
    setNum(this.tScore, sc ? sc[TEAM.T] ?? 0 : 0);
    setNum(this.ctScore, sc ? sc[TEAM.CT] ?? 0 : 0);

    const players = s?.players;
    const n = players ? players.length : 0;
    let nt = 0;
    let nc = 0;
    let ns = 0;

    for (let i = 0; i < n; i++) {
      const p = players[i];
      if (!p) continue;
      let row = null;
      if (p.team === TEAM.T) {
        if (nt < PER_TEAM) row = this.t.rows[nt++];
      } else if (p.team === TEAM.CT) {
        if (nc < PER_TEAM) row = this.ct.rows[nc++];
      } else if (ns < PER_TEAM) {
        row = this.spec.rows[ns++];
      }
      if (row) fillRow(row, p);
    }

    hideFrom(this.t.rows, nt);
    hideFrom(this.ct.rows, nc);
    hideFrom(this.spec.rows, ns);
    setHidden(this.spec.head, ns === 0);
    setHidden(this.spec.cols, ns === 0);
    setHidden(this.spec.body, ns === 0);
    setNum(this.t.count, nt);
    setNum(this.ct.count, nc);
    setNum(this.spec.count, ns);
  }
}

function fillRow(row, p) {
  setStyle(row.node, 'display', '');
  setText(row.name, p.name ?? '');
  setNum(row.k, p.kills ?? 0);
  setNum(row.d, p.deaths ?? 0);
  setNum(row.moneyVal, p.money ?? 0);
  setNum(row.ping, p.ping ?? 0);
  setClass(row.node, 'dead', p.alive === false);
  setClass(row.bomb, 'on', !!p.hasBomb);
}

function hideFrom(rows, from) {
  for (let i = from; i < rows.length; i++) setHidden(rows[i].node, true);
}
